
import { useCallback } from "react";
import { getColumnIndex, getColumnLetter } from "../utils/tableHelpers";

export const useTableOperations = ({
  statementId,
  rows,
  columns,
  setRows,
  setColumns,
  setPendingChanges,
  setEditHistory,
  onDataUpdate,
  showNotification,
  getColumnIndex: externalGetColumnIndex,
  getColumnLetter: externalGetColumnLetter
}) => {
  const getColIndex = externalGetColumnIndex || ((columnId) => getColumnIndex(columns, columnId));
  const getColLetter = externalGetColumnLetter || ((index) => getColumnLetter(index));

  const createEmptyRow = useCallback(() => {
    const row = {};
    columns.forEach(c => (row[c.id] = ""));
    return row;
  }, [columns]);

  // INSERT ROW - ADD TO PENDING CHANGES
  const insertRowAbove = useCallback((rowIndex) => {
    const newRow = createEmptyRow();
    
    const updated = [...rows];
    updated.splice(rowIndex, 0, newRow);
    setRows(updated);
    onDataUpdate?.(updated);
    
    const change = {
      id: Date.now(),
      type: 'insert_row',
      row_index: rowIndex + 1,
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => [...prev, change]);
    setEditHistory(prev => [...prev, change]);
    
    showNotification('Row inserted above', 'info');
  }, [rows, columns, setRows, onDataUpdate, createEmptyRow, setPendingChanges, setEditHistory, showNotification]);

  const insertRowBelow = useCallback((rowIndex) => {
    const targetRowIndex = rowIndex + 1;
    const newRow = createEmptyRow();
    
    const updated = [...rows];
    updated.splice(targetRowIndex, 0, newRow);
    setRows(updated);
    onDataUpdate?.(updated);
    
    const change = {
      id: Date.now(),
      type: 'insert_row',
      row_index: targetRowIndex + 1,
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => [...prev, change]);
    setEditHistory(prev => [...prev, change]);
    
    showNotification('Row inserted below', 'info');
  }, [rows, columns, setRows, onDataUpdate, createEmptyRow, setPendingChanges, setEditHistory, showNotification]);

  // DELETE ROW - ADD TO PENDING CHANGES
  const deleteRow = useCallback((rowIndex) => {
    const rowData = rows[rowIndex];
    
    const updated = rows.filter((_, i) => i !== rowIndex);
    setRows(updated);
    onDataUpdate?.(updated);
    
    const change = {
      id: Date.now(),
      type: 'delete_row',
      row_index: rowIndex + 1,
      deleted_data: rowData,
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => [...prev, change]);
    setEditHistory(prev => [...prev, change]);
    
    showNotification('Row deleted', 'info');
  }, [rows, setRows, onDataUpdate, setPendingChanges, setEditHistory, showNotification]);

  // INSERT COLUMN - ADD TO PENDING CHANGES
  const insertColumn = useCallback((position, referenceColumnId) => {
    const columnId = `col_${Date.now()}`;
    const index = getColIndex(referenceColumnId);
    const insertIndex = position === "left" ? index : index + 1;
    
    const newCol = {
      id: columnId,
      label: "New Column",
      width: 120
    };

    const updatedCols = [...columns];
    updatedCols.splice(insertIndex, 0, newCol);
    setColumns(updatedCols);

    const updatedRows = rows.map(r => ({
      ...r,
      [columnId]: ""
    }));
    setRows(updatedRows);
    onDataUpdate?.(updatedRows);
    
    const columnLetter = getColLetter(insertIndex);
    const change = {
      id: Date.now(),
      type: 'insert_column',
      column_index: insertIndex + 1,
      column_letter: columnLetter,
      header: "New Column",
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => [...prev, change]);
    setEditHistory(prev => [...prev, change]);
    
    showNotification('Column inserted', 'info');
  }, [rows, columns, setRows, setColumns, onDataUpdate, getColIndex, getColLetter, setPendingChanges, setEditHistory, showNotification]);

  // DELETE COLUMN - ADD TO PENDING CHANGES
  const deleteColumn = useCallback((columnId) => {
    const columnIndex = getColIndex(columnId);
    
    if (columns.length <= 1) {
      showNotification("Cannot delete the last column!", "error");
      return;
    }
    
    const columnData = rows.map(row => row[columnId]);
    
    const updatedCols = columns.filter(c => c.id !== columnId);
    const updatedRows = rows.map(row => {
      const newRow = { ...row };
      delete newRow[columnId];
      return newRow;
    });
    
    setColumns(updatedCols);
    setRows(updatedRows);
    onDataUpdate?.(updatedRows);
    
    const columnLetter = getColLetter(columnIndex);
    const change = {
      id: Date.now(),
      type: 'delete_column',
      column_index: columnIndex + 1,
      column_letter: columnLetter,
      deleted_data: columnData,
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => [...prev, change]);
    setEditHistory(prev => [...prev, change]);
    
    showNotification('Column deleted', 'info');
  }, [rows, columns, setRows, setColumns, onDataUpdate, getColIndex, getColLetter, setPendingChanges, setEditHistory, showNotification]);

  return {
    insertRowAbove,
    insertRowBelow,
    deleteRow,
    insertColumn,
    deleteColumn
  };
};
